// Piezas de modelado comunes de la familia de entradas: placas con serigrafía, conectores macho
// (rectos y en ángulo recto), componentes SMD y circuitos integrados con su rotulado.
import * as THREE from 'three';
import { M, mat, canvas, texture, text, extrudeBoard, boardShape, cachedGeo } from '../kit.js';
import { PITCH } from '../../parts/registry.js';

export { PITCH };
export const SILK = '#f2f4f2';

const texCache = new Map();

/** Geometría compartida entre instancias (no se libera al borrar una pieza). */
export function sharedGeo(key, make) {
  return cachedGeo('in:' + key, () => {
    const g = make();
    g.userData.shared = true;
    return g;
  });
}

/** Textura compartida por clave (serigrafías y rótulos que no cambian entre instancias). */
export function sharedTexture(key, w, d, scale, draw) {
  let t = texCache.get(key);
  if (!t) {
    const { c, g } = canvas(w, d, scale);
    draw(g);
    t = texture(c);
    t.userData.shared = true;
    texCache.set(key, t);
  }
  return t;
}

/**
 * Placa de circuito impreso con serigrafía. draw(g, X, Y) dibuja en mm: X(x) e Y(z) pasan de
 * coordenadas locales de la pieza a coordenadas del lienzo.
 */
export function pcb(b, { key, w, d, t = 1.6, y0, color, edge, holes = [], r = 0.8, scale = 20, draw }) {
  const X = (x) => x + w / 2;
  const Y = (z) => z + d / 2;
  const tex = sharedTexture('pcb:' + key, w, d, scale, (g) => {
    g.fillStyle = color;
    g.fillRect(0, 0, w, d);
    if (draw) draw(g, X, Y);
  });
  b.root.add(extrudeBoard(boardShape(w, d, { r }), t, tex, edge, { holes: holes.map((h) => [...h]), w, d, y0 }));
  for (const [x, z, hr] of holes) {
    b.add(cachedGeo(`ring${hr}`, () => new THREE.RingGeometry(hr, hr + 0.9, 24).rotateX(-Math.PI / 2)), M.tin, { x, y: y0 + t + 0.02, z });
  }
}

/** Pad redondo estañado (agujero metalizado o soldadura) sobre la placa. */
export function pad(b, x, z, y, { r = 0.85, hole = 0.5 } = {}) {
  b.add(cachedGeo(`pad${r},${hole}`, () => new THREE.RingGeometry(hole, r, 18).rotateX(-Math.PI / 2)), M.tin, { x, y: y + 0.03, z });
  b.add(cachedGeo(`padh${hole}`, () => new THREE.CircleGeometry(hole, 14).rotateX(-Math.PI / 2)), M.hole, { x, y: y + 0.02, z });
}

/** Punto de soldadura (cono de estaño) donde un pin atraviesa la placa. */
export function solder(b, x, z, y) {
  b.add(cachedGeo('solder', () => new THREE.CylinderGeometry(0.42, 0.95, 0.7, 12)), M.tin, { x, y: y + 0.35, z });
}

/**
 * Conector macho recto hacia arriba: base de plástico sobre la placa y pines dorados hasta tipY.
 * zs/xs: posiciones de los pines (columna en Z o fila en X).
 */
export function headerUp(b, pts, { y, tipY, below = 2.0, base = 2.5 }) {
  const xs = pts.map((p) => p[0]);
  const zs = pts.map((p) => p[1]);
  const alongZ = Math.max(...zs) - Math.min(...zs) > Math.max(...xs) - Math.min(...xs);
  const n = pts.length;
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
  const len = n * PITCH;
  b.box(alongZ ? PITCH : len, base, alongZ ? len : PITCH, M.blackPlastic, cx, y, cz);
  for (const [x, z] of pts) {
    b.box(0.64, tipY - y + below, 0.64, M.gold, x, y - below, z);
    b.add(cachedGeo('pintip', () => new THREE.ConeGeometry(0.45, 0.5, 4)), M.gold, { x, y: tipY + 0.2, z, ry: Math.PI / 4 });
  }
}

/**
 * Conector macho en ángulo recto: patas verticales en la placa (x = bendX) y pines horizontales
 * hacia dir (−1 o +1) hasta tipX, a la altura y de los pines.
 */
export function headerSide(b, zs, { bendX, tipX, y, top, dir = -1, below = 1.6 }) {
  const len = zs.length * PITCH;
  const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
  const bodyX = bendX + dir * 1.55;
  b.box(2.5, 2.54, len, M.blackPlastic, bodyX, top, cz);
  const hLen = Math.abs(tipX - bendX);
  for (const z of zs) {
    b.box(hLen, 0.64, 0.64, M.gold, (bendX + tipX) / 2, y - 0.32, z);
    b.box(0.64, y - top + below + 0.32, 0.64, M.gold, bendX, top - below, z);
  }
}

/** Resistencia o capacitor SMD (cuerpo con terminales estañados). */
export function smd(b, x, z, y, { w = 1.6, d = 0.8, h = 0.45, color = '#1c1c1e', rot = 0 } = {}) {
  const body = mat(color, { roughness: 0.55 });
  const ry = rot;
  const ex = Math.cos(ry) * (w / 2 - 0.15);
  const ez = -Math.sin(ry) * (w / 2 - 0.15);
  b.box(w - 0.5, h, d, body, x, y, z, { ry });
  b.box(0.3, h + 0.02, d + 0.02, M.tin, x + ex, y, z + ez, { ry });
  b.box(0.3, h + 0.02, d + 0.02, M.tin, x - ex, y, z - ez, { ry });
}

/** Pads vacíos de un componente SMD que el fabricante no soldó. */
export function emptyPads(b, x, z, y, { w = 1.6, d = 0.8, rot = 0 } = {}) {
  const ex = Math.cos(rot) * (w / 2 - 0.2);
  const ez = -Math.sin(rot) * (w / 2 - 0.2);
  for (const s of [-1, 1]) b.box(0.55, 0.06, d, M.tin, x + s * ex, y, z + s * ez, { ry: rot });
}

/** Rótulo de un circuito integrado (textura con texto gris sobre negro y la marca del pin 1). */
export function chipTexture(lines, w, d, { dot = true, color = '#1b1b1e', ink = '#a8aab0' } = {}) {
  return sharedTexture('chip:' + lines.join('|') + w + 'x' + d, w, d, 40, (g) => {
    g.fillStyle = color;
    g.fillRect(0, 0, w, d);
    const n = lines.length;
    const size = Math.min(d / (n + 1.2), (w / Math.max(...lines.map((l) => l.length))) * 1.55);
    lines.forEach((l, i) => text(g, l, w / 2, d / 2 + (i - (n - 1) / 2) * size * 1.15, { size, color: ink, weight: 600 }));
    if (dot) {
      g.fillStyle = '#0d0d0f';
      g.beginPath();
      g.arc(Math.min(1.0, w * 0.12), d - Math.min(1.0, d * 0.18), Math.min(0.42, d * 0.1), 0, Math.PI * 2);
      g.fill();
    }
  });
}

/** Tapa con textura (rótulos de chips, serigrafía de piezas) apoyada sobre una cara en y. */
export function label(root, tex, w, d, { x = 0, y, z = 0, ry = 0, roughness = 0.5 }) {
  const m = new THREE.Mesh(sharedGeo(`lbl${w},${d}`, () => new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2)), new THREE.MeshStandardMaterial({ map: tex, roughness }));
  m.position.set(x, y + 0.01, z);
  m.rotation.y = ry;
  m.receiveShadow = true;
  root.add(m);
  return m;
}

/** Circuito integrado SOIC/SOP con patas de ala de gaviota en los lados largos. */
export function soic(b, root, x, z, y, { pins = 8, w = 4.9, d = 3.9, h = 1.5, lines = [], rot = 0, wide = false }) {
  const per = pins / 2;
  const pitch = 1.27;
  const legLen = wide ? 1.1 : 1.0;
  const ry = rot;
  const c = Math.cos(ry);
  const s = Math.sin(ry);
  const at = (lx, lz) => [x + lx * c + lz * s, z - lx * s + lz * c];
  b.box(w, h, d, M.chip, x, y + 0.1, z, { ry });
  for (let i = 0; i < per; i++) {
    const lx = (i - (per - 1) / 2) * pitch;
    for (const side of [-1, 1]) {
      const [px, pz] = at(lx, side * (d / 2 + legLen / 2 - 0.05));
      b.box(0.42, 0.2, legLen, M.tin, px, y, pz, { ry });
      const [qx, qz] = at(lx, side * (d / 2 + 0.05));
      b.box(0.42, 0.55, 0.25, M.tin, qx, y + 0.15, qz, { ry });
    }
  }
  if (lines.length) label(root, chipTexture(lines, w, d), w, d, { x, y: y + 0.1 + h, z, ry });
}

/** Encapsulado SOT-23 (transistores y reguladores pequeños). */
export function sot23(b, x, z, y, { rot = 0 } = {}) {
  b.box(2.9, 1.0, 1.3, M.chip, x, y + 0.08, z, { ry: rot });
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  for (const [lx, lz] of [[-0.95, 1.0], [0.95, 1.0], [0, -1.0]]) b.box(0.4, 0.15, 0.6, M.tin, x + lx * c + lz * s, y, z - lx * s + lz * c, { ry: rot });
}

/** Cable con funda (tubo) que pasa por una lista de puntos. */
export function cable(b, points, r, color) {
  const curve = new THREE.CatmullRomCurve3(points.map((p) => new THREE.Vector3(...p)));
  b.add(new THREE.TubeGeometry(curve, Math.max(12, points.length * 10), r, 8, false), mat(color, { roughness: 0.45 }));
}

/** Rótulo de texto blanco sobre fondo transparente (para serigrafía sobre piezas ya construidas). */
export function textDecal(root, key, lines, w, d, { x = 0, y, z = 0, ry = 0, size = 2, color = SILK, weight = 700 }) {
  const tex = sharedTexture('decal:' + key, w, d, 24, (g) => {
    lines.forEach((l, i) => text(g, l, w / 2, d / 2 + (i - (lines.length - 1) / 2) * size * 1.2, { size, color, weight }));
  });
  const m = new THREE.Mesh(sharedGeo(`dec${w},${d}`, () => new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2)), new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
  m.position.set(x, y + 0.02, z);
  m.rotation.y = ry;
  m.userData.noPick = true;
  root.add(m);
  return m;
}
